import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Coins, Megaphone, Plus } from 'lucide-react';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Badge } from '@atoms/Badge';
import { Spinner } from '@atoms/Spinner';
import { Modal } from '@atoms/Modal';
import { wholesalerService } from '@services/wholesalerService';
import { storageService } from '@services/storageService';
import { useAuthStore } from '@store/authStore';
import { AD_STATUS_LABELS, formatShortDate } from '@utils/formatters';

const DURATION_PRESETS = [1, 3, 7, 15, 30] as const;

function calculateAdCost(durationDays: number, creditsPerDay: number) {
  return Math.max(1, durationDays) * creditsPerDay;
}

export function AdsSection() {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const { refreshUser } = useAuthStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    buttonLink: '/login',
    durationDays: '1',
    image: '',
  });

  const { data: ads, isLoading: adsLoading } = useQuery({
    queryKey: ['wholesaler', 'ads'],
    queryFn: () => wholesalerService.getAds(),
  });

  const { data: pricing, isLoading: pricingLoading } = useQuery({
    queryKey: ['wholesaler', 'ad-pricing'],
    queryFn: () => wholesalerService.getAdPricing(),
  });

  const creditsPerDay = pricing?.creditsPerDay ?? 1;
  const adCredits = pricing?.adCredits ?? 0;
  const durationDays = Math.max(1, Number(form.durationDays) || 1);
  const totalCost = useMemo(
    () => calculateAdCost(durationDays, creditsPerDay),
    [durationDays, creditsPerDay],
  );
  const insufficientCredits = totalCost > adCredits;

  const requestMutation = useMutation({
    mutationFn: () =>
      wholesalerService.requestAd({
        title: form.title,
        subtitle: form.subtitle || undefined,
        buttonLink: form.buttonLink || '/login',
        image: form.image,
        durationDays,
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'ads'] });
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'ad-pricing'] });
      await refreshUser();
      setForm({ title: '', subtitle: '', buttonLink: '/login', durationDays: '1', image: '' });
      setModalOpen(false);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => storageService.uploadImage(file),
    onSuccess: (url) => setForm((prev) => ({ ...prev, image: url })),
  });

  if (adsLoading || pricingLoading) return <Spinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reklam Yönetimi</h1>
          <p className="mt-1 text-sm text-slate-500">
            Ana sayfada öne çıkmak için reklam talebi oluşturun. Yönetici onayından sonra yayınlanır.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-amber-900">
            <Coins className="h-4 w-4" />
            <span className="text-sm font-medium">Reklam Kredisi:</span>
            <strong>{adCredits}</strong>
          </div>
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Yeni Reklam Talebi
          </Button>
        </div>
      </div>

      <Card className="card-elevated overflow-hidden">
        <div className="border-b border-slate-100 px-4 py-3">
          <h2 className="font-semibold text-slate-900">Reklam Taleplerim</h2>
          <p className="text-sm text-slate-500">{(ads ?? []).length} kayıt</p>
        </div>
        {!ads?.length ? (
          <div className="py-12 text-center text-slate-500">
            <Megaphone className="mx-auto mb-3 h-10 w-10 opacity-30" />
            <p>Henüz reklam talebiniz bulunmuyor.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3">Görsel</th>
                  <th className="px-4 py-3">Başlık</th>
                  <th className="px-4 py-3">Süre</th>
                  <th className="px-4 py-3">Durum</th>
                  <th className="px-4 py-3">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => (
                  <tr key={ad.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <img src={ad.image} alt={ad.title} className="h-12 w-24 rounded-lg object-cover" />
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{ad.title}</p>
                      {ad.subtitle && <p className="text-xs text-slate-500">{ad.subtitle}</p>}
                      {ad.buttonLink && (
                        <p className="mt-1 text-xs text-slate-400">Link: {ad.buttonLink}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">{Math.ceil((ad.durationHours ?? 24) / 24)} gün</td>
                    <td className="px-4 py-3">
                      <Badge variant={ad.status === 'approved' ? 'success' : ad.status === 'pending' ? 'warning' : 'danger'}>
                        {AD_STATUS_LABELS[ad.status]}
                      </Badge>
                      {ad.rejectionReason && (
                        <p className="mt-1 text-xs text-red-600">Red: {ad.rejectionReason}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{formatShortDate(ad.createdAt!)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Yeni Reklam Talebi">
        <div className="space-y-4">
          <div className="rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-900">
            Maliyet: <strong>{totalCost}</strong> reklam kredisi ({creditsPerDay} kredi/gün × {durationDays} gün).
            Mevcut bakiye: <strong>{adCredits}</strong>
          </div>

          <Input
            label="Reklam Başlığı"
            placeholder="Örn: Haftalık Meyve Kampanyası"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
          />
          <Input
            label="Alt Başlık"
            placeholder="Örn: Tüm ürünlerde %20 indirim"
            value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
          />
          <Input
            label="Tıklanınca Gidilecek Link"
            placeholder="/login"
            value={form.buttonLink}
            onChange={(e) => setForm({ ...form, buttonLink: e.target.value })}
          />

          <div>
            <Input
              label="Yayın Süresi (Gün)"
              type="number"
              min={1}
              value={form.durationDays}
              onChange={(e) => setForm({ ...form, durationDays: e.target.value })}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              {DURATION_PRESETS.map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => setForm({ ...form, durationDays: String(days) })}
                  className={`rounded-lg border px-3 py-1 text-xs font-medium ${
                    Number(form.durationDays) === days
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {days} Gün
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Reklam Görseli (1200×400 önerilir)
            </label>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadMutation.mutate(file);
              }}
            />
            <Button type="button" variant="outline" loading={uploadMutation.isPending} onClick={() => fileRef.current?.click()}>
              Görsel Yükle
            </Button>
            {form.image && (
              <img src={form.image} alt="Önizleme" className="mt-3 h-28 w-full max-w-md rounded-lg object-cover" />
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              className="flex-1"
              loading={requestMutation.isPending}
              disabled={!form.title || !form.image || insufficientCredits}
              onClick={() => requestMutation.mutate()}
            >
              {insufficientCredits ? 'Yetersiz Kredi' : 'Talebi Gönder'}
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setModalOpen(false)}>
              İptal
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
